import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { compressImage } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, User, Phone, Clock, FileText,
  Upload, CheckCircle, AlertCircle, MessageCircle, Loader2, Home
} from 'lucide-react';

const STATUS_COLORS = { levantada: '#3B82F6', en_proceso: '#F59E0B', turnada: '#8B5CF6', resuelta: '#10B981', cancelada: '#EF4444' };
const STATUS_LABELS = { levantada: 'Levantada', en_proceso: 'En Proceso', turnada: 'Turnada a Alcaldia', resuelta: 'Resuelta', cancelada: 'Cancelada' };
const STATUS_ICONS = { levantada: AlertCircle, en_proceso: Clock, turnada: FileText, resuelta: CheckCircle, cancelada: AlertCircle };

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function TimelineEntry({ entry }) {
  const Icon = STATUS_ICONS[entry.status] || Clock;
  const color = STATUS_COLORS[entry.status] || '#52525B';
  return (
    <div className="relative flex gap-4 pl-1" data-testid={`timeline-${entry.status}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center z-10 flex-shrink-0" style={{ backgroundColor: `${color}15`, border: `2px solid ${color}` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="flex-1 pb-1">
        <p className="text-sm font-semibold text-[#0A0A0A]">{STATUS_LABELS[entry.status] || entry.status}</p>
        <p className="text-xs text-[#52525B] mt-0.5">{entry.nota}</p>
        <p className="text-xs text-[#52525B] mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {formatDate(entry.fecha)} — {entry.usuario}
        </p>
      </div>
    </div>
  );
}

function NotifEntry({ notif }) {
  return (
    <div className="relative flex gap-4 pl-1" data-testid={`notif-${notif.id}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center z-10 flex-shrink-0 bg-[#25D366]/10 border-2 border-[#25D366]">
        <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
      </div>
      <div className="flex-1 pb-1">
        <p className="text-sm font-semibold text-[#25D366]">WhatsApp {notif.status === 'enviado' ? 'enviado' : notif.status === 'error' ? '(error)' : '(mock)'}</p>
        <p className="text-xs text-[#52525B] mt-0.5">{notif.mensaje}</p>
        <p className="text-xs text-[#52525B] mt-1">{formatDate(notif.created_at)}</p>
      </div>
    </div>
  );
}

function StatusUpdateForm({ gestion, api, gestionId, onUpdated }) {
  const [newStatus, setNewStatus] = useState('');
  const [nota, setNota] = useState('');
  const [updating, setUpdating] = useState(false);

  const updateStatus = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await api.put(`/gestiones/${gestionId}/status`, { status: newStatus, nota });
      toast.success('Estado actualizado');
      setNewStatus('');
      setNota('');
      onUpdated();
    } catch {
      toast.error('Error actualizando estado');
    } finally {
      setUpdating(false);
    }
  };

  const uploadAcuse = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = file.type?.startsWith('image/') ? await compressImage(file, 1024, 0.8) : file;
    const fd = new FormData();
    fd.append('file', compressed);
    try {
      await api.post(`/gestiones/${gestionId}/acuse`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Acuse subido');
      onUpdated();
    } catch {
      toast.error('Error subiendo acuse');
    }
  };

  return (
    <Card className="bg-white border border-[#E4E4E7] rounded-md">
      <CardHeader>
        <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Actualizar Estado</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={newStatus} onValueChange={setNewStatus}>
          <SelectTrigger data-testid="update-status-select" className="h-12 rounded-md border-[#E4E4E7]"><SelectValue placeholder="Nuevo estado" /></SelectTrigger>
          <SelectContent>
            {gestion.status === 'levantada' && <SelectItem value="en_proceso">En Proceso</SelectItem>}
            {gestion.status === 'levantada' && <SelectItem value="turnada">Turnada a Alcaldia</SelectItem>}
            {(gestion.status === 'en_proceso' || gestion.status === 'turnada') && <SelectItem value="resuelta">Resuelta</SelectItem>}
            {gestion.status !== 'resuelta' && gestion.status !== 'cancelada' && <SelectItem value="cancelada">Cancelada</SelectItem>}
          </SelectContent>
        </Select>
        <Textarea data-testid="update-nota-input" className="rounded-md border-[#E4E4E7]" value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota..." />
        <label className="flex items-center gap-2 text-sm text-[#0052FF] cursor-pointer hover:underline">
          <Upload className="w-4 h-4" /> Subir acuse sellado
          <input data-testid="acuse-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={uploadAcuse} />
        </label>
        {gestion.acuse_path && <p className="text-xs text-[#10B981] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Acuse ya subido</p>}
        <Button data-testid="update-status-btn" onClick={updateStatus} disabled={!newStatus || updating} className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold">
          {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
          Actualizar Estado
        </Button>
      </CardContent>
    </Card>
  );
}

export default function GestionDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const [gestion, setGestion] = useState(null);
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [gRes, nRes] = await Promise.all([
        api.get(`/gestiones/${id}`),
        api.get(`/notificaciones?gestion_id=${id}`)
      ]);
      setGestion(gRes.data);
      setNotificaciones(nRes.data);
    } catch {
      toast.error('Error cargando gestion');
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>;
  if (!gestion) return <div className="text-center py-16"><p className="text-lg font-semibold text-[#0A0A0A]">Gestion no encontrada</p></div>;

  const canUpdateStatus = user?.role === 'gestion' || user?.role === 'admin';
  const isTerminal = gestion.status === 'resuelta' || gestion.status === 'cancelada';

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="gestion-detail-page">
      <Link to="/mis-gestiones" className="inline-flex items-center gap-2 text-sm text-[#52525B] hover:text-[#0052FF] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Volver
      </Link>

      <div>
        <div className="flex items-center gap-3 mb-2">
          <Badge className="text-xs" style={{ backgroundColor: `${STATUS_COLORS[gestion.status]}15`, color: STATUS_COLORS[gestion.status], border: `1px solid ${STATUS_COLORS[gestion.status]}30` }}>
            {STATUS_LABELS[gestion.status]}
          </Badge>
          <span className="text-xs text-[#52525B] font-mono">#{gestion.id?.slice(0, 8)}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {gestion.tipo}{gestion.tipo === 'Otro' && gestion.tipo_otro ? `: ${gestion.tipo_otro}` : ''}
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-5 space-y-3">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Ubicacion del reporte</p>
            {gestion.calle ? (
              <p className="flex items-center gap-2 text-sm text-[#0A0A0A]"><MapPin className="w-4 h-4 text-[#0052FF]" />{gestion.calle} {gestion.numero}, {gestion.colonia}</p>
            ) : (
              <p className="text-sm text-[#F59E0B] font-semibold">Ubicacion pendiente</p>
            )}
            {gestion.lat && gestion.lng && <p className="text-[#52525B] font-mono text-xs ml-6">{gestion.lat.toFixed(6)}, {gestion.lng.toFixed(6)}</p>}
            {gestion.seccion_electoral && <p className="text-xs text-[#52525B] ml-6">Seccion: <span className="font-bold text-[#0A0A0A]">{gestion.seccion_electoral}</span></p>}
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-5 space-y-3">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Vecino</p>
            <p className="flex items-center gap-2 text-sm text-[#0A0A0A]"><User className="w-4 h-4 text-[#0052FF]" /> {gestion.vecino_nombre}</p>
            <p className="flex items-center gap-2 text-sm text-[#0A0A0A]"><Phone className="w-4 h-4 text-[#0052FF]" /> {gestion.vecino_telefono}</p>
            {gestion.vecino_calle && (
              <p className="flex items-center gap-2 text-xs text-[#52525B]"><Home className="w-4 h-4" /> {gestion.vecino_calle} {gestion.vecino_numero}, {gestion.vecino_colonia}</p>
            )}
            <p className="flex items-center gap-2 text-[#52525B] text-xs"><Clock className="w-4 h-4" /> {formatDate(gestion.created_at)}</p>
          </CardContent>
        </Card>
      </div>

      {gestion.descripcion && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Descripcion</p>
            <p className="text-sm text-[#0A0A0A] leading-relaxed">{gestion.descripcion}</p>
          </CardContent>
        </Card>
      )}

      {gestion.fotos?.length > 0 && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3">Evidencia</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gestion.fotos.map(f => (
                <div key={f.file_id} className="aspect-square rounded-md overflow-hidden border border-[#E4E4E7] bg-[#FAFAFA]">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.path}?auth=cookie`} alt={f.filename} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border border-[#E4E4E7] rounded-md">
        <CardHeader><CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Historial</CardTitle></CardHeader>
        <CardContent>
          <div className="relative">
            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-[#E4E4E7]" />
            <div className="space-y-6">
              {gestion.historial?.map(h => <TimelineEntry key={`${h.status}-${h.fecha}`} entry={h} />)}
              {notificaciones.map(n => <NotifEntry key={n.id} notif={n} />)}
            </div>
          </div>
        </CardContent>
      </Card>

      {canUpdateStatus && !isTerminal && (
        <StatusUpdateForm gestion={gestion} api={api} gestionId={id} onUpdated={loadData} />
      )}
    </div>
  );
}
